import { FormEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import Upload from 'hooks/useDirectUpload/Upload';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import {
  PORTAL_COLLECT_FIELDS,
  ReferralFieldDef,
  sourceSystemLabel,
  splitCollectedPatientName,
  VALUE_NAMES,
} from './constants';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  // Values read off the portal elsewhere (the extension's manual HUD) — they land in
  // the entry fields for the person to review, never overwriting what they've typed.
  prefill?: Record<string, string> | null;
}

const TEXT_COLLECT_FIELDS = PORTAL_COLLECT_FIELDS.filter((field) => field.type !== 'date');

// Where to go get the information — the whole instruction the kickoff carried. A URL
// pointer renders as a link (and later becomes the browser-extension hand-off).
function SourceContext({ workflow }: { workflow: Workflow }) {
  const system = workflow.valueFor('source_system')?.value;
  const pointer = workflow.valueFor('retrieval_pointer')?.value;
  if (!system && !pointer) return null;

  return (
    <SourceLine data-testid='retrieve-source-context'>
      {system && <BodySmallBold>{sourceSystemLabel(system)}</BodySmallBold>}
      {pointer &&
        (/^https?:\/\//.test(pointer) ? (
          <PointerLink href={pointer} target='_blank' rel='noreferrer'>
            {pointer}
          </PointerLink>
        ) : (
          <Pointer>{pointer}</Pointer>
        ))}
    </SourceLine>
  );
}

// Document-first manual fallback for the source-portal pull: the person saves the
// referral documents out of the portal and uploads them here — the AI actions read the
// demographics from the documents. The collect-fields are the optional shortcut: any
// value typed here is committed at completion and outranks the downstream extraction,
// so what a person read off the portal is never overwritten by the AI.
export default function RetrievePortalForm({ action, workflow, onComplete, isCompleting, prefill }: Props) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_COLLECT_FIELDS.map((field) => [field.name, '']))
  );
  const [dob, setDob] = useState<Date | null>(null);

  // Prefill fills only fields the person hasn't touched — their own typing wins.
  useEffect(() => {
    if (!prefill) return;
    setText((prev) =>
      Object.fromEntries(Object.entries(prev).map(([name, value]) => [name, value || (prefill[name] ?? '')]))
    );
    if (prefill[VALUE_NAMES.PATIENT_DOB]) {
      setDob((prev) => prev ?? parseDate(prefill[VALUE_NAMES.PATIENT_DOB]) ?? null);
    }
  }, [prefill]);

  const readyUploads = uploads.filter((upload) => upload.signedId);
  const ready = readyUploads.length > 0 && !isUploading && !isCompleting;

  const typedValues = (): WorkflowValuesInput => {
    const values: WorkflowValuesInput = {};
    TEXT_COLLECT_FIELDS.forEach((field) => {
      const value = text[field.name].trim();
      if (value) values[field.name] = value;
    });
    const dashDob = simpleDashDate(dob);
    if (dashDob) values[VALUE_NAMES.PATIENT_DOB] = dashDob;
    return values;
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    onComplete(action.id, {
      attachments: readyUploads.map((upload) => ({ signedId: upload.signedId!, filename: upload.file.name })),
      ...splitCollectedPatientName(typedValues()),
    });
  }

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        <Optional> (optional)</Optional>
      </FieldLabel>
      {field.type === 'date' && <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />}
      {field.type === 'select' && (
        <div data-testid={`retrieve-field-${field.name}`}>
          <Select
            inputId={`retrieve-field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          data-testid={`retrieve-field-${field.name}`}
        />
      )}
    </Field>
  );

  return (
    <Form onSubmit={submit} data-testid='retrieve-portal-form'>
      <SourceContext workflow={workflow} />
      <BodySmall>
        Save the referral documents from the source portal (download or print to PDF), then upload them here. The
        patient&apos;s details are read from the documents.
      </BodySmall>
      <UploadZone data-testid='retrieve-documents'>
        <FileInputComponent onChange={setUploads} onUploading={setIsUploading} renderUploadsInternally />
      </UploadZone>
      <BodySmall>Anything you already know from the portal can be entered directly:</BodySmall>
      <FieldGrid data-testid='retrieve-collect-fields'>{PORTAL_COLLECT_FIELDS.map(renderField)}</FieldGrid>
      <div>
        <Button type='submit' size='small' disabled={!ready} data-testid='retrieve-submit'>
          Save retrieved documents
        </Button>
      </div>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadZone = styled.div`
  margin-top: 4px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Optional = styled.span`
  color: ${colors.black50};
  font-weight: 400;
`;

const SourceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.black5};
`;

const Pointer = styled(BodySmall)`
  color: ${colors.black50};
  word-break: break-all;
`;

const PointerLink = styled.a`
  font-size: 12px;
  color: ${colors.blue100};
  word-break: break-all;
`;
